import { CalendarRange, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import {
	useCreateDDSMonthlyTheme,
	useDDSMonthlyThemes,
	useUpdateDDSMonthlyTheme,
} from '@/api/queries/dds-monthly-themes-queries'
import { DDSMonthlyThemeFormContent } from '@/components/administration'
import {
	AdminCardPage,
	ResponsiveFormModal,
	SearchInput,
} from '@/components/commons'
import { GroupedList, type GroupItem } from '@/components/grouped-list'
import { Button } from '@/components/ui/button'
import { adminBackRoute } from '@/constants/admin-menu'
import { getMonthLabel } from '@/constants/months'
import { ROUTES } from '@/constants/routes'
import type { DDSMonthlyThemeType } from '@/schemas/dds-monthly-theme-schema'
import type {
	DDSMonthlyThemeInput,
	DDSMonthlyThemeOutput,
} from '@/types/dds-monthly-theme'
import { getApiErrorMessage } from '@/utils/api-error'

/** Ordem fixa dos grupos — só entram os tipos que possuem temas cadastrados */
const THEME_TYPE_ORDER: DDSMonthlyThemeType[] = ['DIG', 'DMI']

export const DDSMonthlyThemesPage = () => {
	const navigate = useNavigate()
	const { t, i18n } = useTranslation()

	const [search, setSearch] = useState('')
	const [formOpen, setFormOpen] = useState(false)
	const [editing, setEditing] = useState<DDSMonthlyThemeOutput | undefined>(
		undefined,
	)

	const { data, isLoading } = useDDSMonthlyThemes({ page: 1, limit: 100 })
	const createMutation = useCreateDDSMonthlyTheme()
	const updateMutation = useUpdateDDSMonthlyTheme()

	const themes = useMemo(() => data?.items ?? [], [data?.items])

	/** Busca por tipo, nome do mês ou descrição do tema */
	const filteredThemes = useMemo(() => {
		const q = search.trim().toLowerCase()
		if (!q) return themes

		return themes.filter((theme) =>
			[theme.type, getMonthLabel(theme.month, i18n.language), theme.description]
				.join(' ')
				.toLowerCase()
				.includes(q),
		)
	}, [themes, search, i18n.language])

	/** Grupos do acordeão: um por tipo (DIG/DMI), meses ordenados como filhos */
	const groups = useMemo<GroupItem[]>(
		() =>
			THEME_TYPE_ORDER.filter((type) =>
				filteredThemes.some((theme) => theme.type === type),
			).map((type) => ({
				id: type,
				name: type,
				children: filteredThemes
					.filter((theme) => theme.type === type)
					.sort((a, b) => a.month - b.month)
					.map((theme) => ({
						id: theme.id,
						name: getMonthLabel(theme.month, i18n.language),
						description: theme.description,
						badgeLabel: String(theme.month),
					})),
			})),
		[filteredThemes, i18n.language],
	)

	const total = filteredThemes.length

	const handleOpenCreate = () => {
		setEditing(undefined)
		setFormOpen(true)
	}

	const handleOpenEdit = (id: string) => {
		setEditing(themes.find((theme) => theme.id === id))
		setFormOpen(true)
	}

	const handleSave = async (values: DDSMonthlyThemeInput) => {
		try {
			if (editing) {
				await updateMutation.mutateAsync({ id: editing.id, body: values })
				toast.success(t('administration.ddsMonthlyThemes.editSuccess'))
			} else {
				await createMutation.mutateAsync(values)
				toast.success(t('administration.ddsMonthlyThemes.createSuccess'))
			}
			setFormOpen(false)
			setEditing(undefined)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	const formTitle = editing
		? t('administration.ddsMonthlyThemes.editTheme')
		: t('administration.ddsMonthlyThemes.createNew')

	return (
		<AdminCardPage
			icon={<CalendarRange className="size-5" />}
			title={t('administration.ddsMonthlyThemes.title')}
			subtitle={t('administration.ddsMonthlyThemes.subtitle')}
			onBack={() =>
				navigate(adminBackRoute(ROUTES.ADMINISTRATION.DDS_MONTHLY_THEMES))
			}
			footerText={`${total} ${
				total === 1
					? t('administration.ddsMonthlyThemes.countSingular')
					: t('administration.ddsMonthlyThemes.countPlural')
			}`}
			actions={
				<Button
					onClick={handleOpenCreate}
					className="gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 w-full sm:w-auto"
				>
					<Plus className="size-4" />
					<span>{t('administration.ddsMonthlyThemes.createNew')}</span>
				</Button>
			}
			modal={
				<ResponsiveFormModal
					open={formOpen}
					onOpenChange={setFormOpen}
					title={formTitle}
					dialogClassName="max-h-[85vh] overflow-y-auto sm:max-w-lg"
				>
					<DDSMonthlyThemeFormContent
						key={editing?.id ?? 'new'}
						mode={editing ? 'edit' : 'create'}
						initial={editing}
						onSave={handleSave}
						onClose={() => setFormOpen(false)}
					/>
				</ResponsiveFormModal>
			}
		>
			<SearchInput
				value={search}
				onChange={setSearch}
				placeholder={t('administration.ddsMonthlyThemes.searchPlaceholder')}
			/>

			{isLoading ? (
				<div className="p-6 text-center text-sm text-muted-foreground">
					{t('administration.ddsMonthlyThemes.loading')}
				</div>
			) : (
				<GroupedList
					groups={groups}
					search={search}
					badgeClass="bg-kaefer-medium-grey/15 text-kaefer-medium-grey"
					childBadgeClass="bg-kaefer-medium-grey/15 text-kaefer-medium-grey"
					onEditChild={(child) => handleOpenEdit(child.id)}
					emptyMessage={t('administration.ddsMonthlyThemes.noResults')}
					emptyIcon={
						<CalendarRange className="size-8 text-kaefer-light-silver" />
					}
				/>
			)}
		</AdminCardPage>
	)
}
